import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import denmsReducer from '_store/denm/denmsReducers';
import { DenmSourceFilter } from '_store/denm/denmsTypes';
import {
  causesSelector,
  denmFilterAlwaysShowActiveEventsSelector,
  denmsSelector,
  paginationCurrentPageSelector,
  paginationItemsPerPageSelector,
  receivedDenmNotificationsSelector,
  subCausesSelector,
} from '_store/denm/selectors';
import { defaultIcon, denmStatuses } from '_store/denm/utils';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { useLocalization } from '@fluent/react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import denmIcons from '../../icons/denm';
import { iconForCause } from '../../icons/utils/denm';
import { toLatLng } from '../../routes/MapView/utils';

const denmSourceLabelKey = (source: DenmSourceFilter): string => {
  switch (source) {
    case DenmSourceFilter.EMERAS:
      return 'forms-event-source-emeras';
    case DenmSourceFilter.CONNEX:
      return 'forms-event-source-vehicles';
    case DenmSourceFilter.DATEXII:
      return 'forms-event-source-datexii';
    case DenmSourceFilter.TMS_UPER:
      return 'forms-event-source-tms-uper';
    default:
      return 'forms-event-source-emeras';
  }
};

const DenmCreatedNotification: FC = () => {
  const dispatch = useAppDispatch();
  const { l10n } = useLocalization();
  const receivedDenmNotifications = useSelector(receivedDenmNotificationsSelector);
  const denms = useSelector(denmsSelector);
  const causes = useSelector(causesSelector);
  const subCauses = useSelector(subCausesSelector);
  const alwaysShowActiveEvents = useSelector(denmFilterAlwaysShowActiveEventsSelector);
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);
  const currentPage = useSelector(paginationCurrentPageSelector);
  const itemsPerPage = useSelector(paginationItemsPerPageSelector);
  const denmFilters = useSelector((state: RootState) => state.denms);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);
  const [warningDenmId, setWarningDenmId] = useState<string | null>(null);

  useEffect(() => {
    const timeoutIds: number[] = [];

    receivedDenmNotifications.forEach((denm) => {
      const currentDenm = denms.find((item) => item.id === denm.id);
      if (!currentDenm) {
        dispatch(denmsReducer.actions.dismissReceivedDenmNotification(denm.id));
        if (warningDenmId === denm.id) {
          setWarningDenmId(null);
        }
        return;
      }

      const { isDenmActive } = denmStatuses(currentDenm);
      if (!isDenmActive) {
        dispatch(denmsReducer.actions.dismissReceivedDenmNotification(denm.id));
        if (warningDenmId === denm.id) {
          setWarningDenmId(null);
        }
        return;
      }

      const expiresAtTimestamp = new Date(denm.expiresAt).getTime();
      const timeUntilExpire = expiresAtTimestamp - Date.now();

      if (timeUntilExpire <= 0) {
        dispatch(denmsReducer.actions.dismissReceivedDenmNotification(denm.id));
        if (warningDenmId === denm.id) {
          setWarningDenmId(null);
        }
        return;
      }

      const timeoutId = window.setTimeout(() => {
        dispatch(denmsReducer.actions.dismissReceivedDenmNotification(denm.id));
        if (warningDenmId === denm.id) {
          setWarningDenmId(null);
        }
      }, timeUntilExpire);
      timeoutIds.push(timeoutId);
    });

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [denms, dispatch, receivedDenmNotifications, warningDenmId]);

  if (receivedDenmNotifications.length === 0) {
    return null;
  }

  const dismissNotification = (denmId: string) => {
    dispatch(denmsReducer.actions.dismissReceivedDenmNotification(denmId));
  };

  const openDenmDetails = (denmId: string) => {
    const denm =
      denms.find((item) => item.id === denmId) ??
      receivedDenmNotifications.find((item) => item.id === denmId);

    if (denm?.referencePoint?.coordinates && mapRef) {
      const markerPosition = toLatLng(denm.referencePoint.coordinates);
      mapRef.flyTo(markerPosition, 15, { animate: true, duration: 1 });
    }

    setTimeout(() => {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DENM,
          id: denmId,
        }),
      );
      dismissNotification(denmId);
    }, 1000);
  };

  const enableFilterAndOpenDetails = () => {
    if (!warningDenmId) {
      return;
    }
    dispatch(
      denmsReducer.actions.denmsRequest({
        status: denmFilters.statusFilter,
        roadSegmentIds: selectedRoadSegmentIds,
        date: denmFilters.dateFilter,
        sources: denmFilters.sourceFilter,
        causeId: denmFilters.causeFilter,
        subCauseId: denmFilters.subCauseFilter,
        updatedFrom: denmFilters.updatedFromFilter,
        updatedTo: denmFilters.updatedToFilter,
        sincroDispatchable: denmFilters.sincroDispatchableFilter,
        excludeCauseAndSubcause: denmFilters.excludeCauseAndSubcauseFilter,
        alwaysShowActiveEvents: true,
        currentPage: currentPage ?? 1,
        itemsPerPage: itemsPerPage ?? 10,
      }),
    );
    const denmId = warningDenmId;
    setWarningDenmId(null);
    openDenmDetails(denmId);
  };

  const inspectDenm = (denmId: string) => {
    if (alwaysShowActiveEvents) {
      openDenmDetails(denmId);
      return;
    }
    setWarningDenmId(denmId);
  };

  return (
    <>
      {receivedDenmNotifications.map((denm) => (
        (() => {
          const subCause = subCauses.find((sc) => sc.id === denm.subCauseId);
          let cause = causes.find((c) => c.id === denm.causeId);
          if (!cause && subCause) {
            cause = causes.find((c) => c.id === subCause.causeId);
          }
          const causeIcon = iconForCause(cause, subCause) ?? defaultIcon;
          const sourceLabel = l10n.getString(denmSourceLabelKey(denm.source));

          return (
            <Paper
              key={denm.id}
              elevation={4}
              style={{
                width: 360,
                maxWidth: 'calc(100vw - 32px)',
                padding: 12,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle1">
                  {l10n.getString('forms-denm-notification-title', { source: sourceLabel })}
                </Typography>
                <IconButton size="small" onClick={() => dismissNotification(denm.id)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box display="flex" alignItems="center" marginBottom={0.5}>
                <img
                  src={denmIcons[causeIcon]?.png}
                  alt={cause?.description ?? 'DENM'}
                  style={{ width: 18, height: 18, marginRight: 8 }}
                />
                <Typography variant="body2">
                  {`${cause?.description ?? '-'} - ${subCause?.description ?? '-'}`}
                </Typography>
              </Box>
              <Typography variant="caption" style={{ opacity: 0.75, display: 'block', marginBottom: 10 }}>
                {denm.updatedAt ? new Date(denm.updatedAt).toLocaleString() : '-'}
              </Typography>
              <Button size="small" color="primary" variant="outlined" onClick={() => inspectDenm(denm.id)}>
                {l10n.getString('forms-denm-notification-inspect')}
              </Button>
            </Paper>
          );
        })()
      ))}

      <Dialog open={Boolean(warningDenmId)} onClose={() => setWarningDenmId(null)}>
        <DialogTitle>{l10n.getString('forms-denm-notification-warning-title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {l10n.getString('forms-denm-notification-warning-body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDenmId(null)} color="secondary">
            {l10n.getString('forms-warning-mess-btn-cancel')}
          </Button>
          <Button onClick={enableFilterAndOpenDetails} color="primary">
            {l10n.getString('forms-denm-notification-warning-confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(DenmCreatedNotification);
